import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { hasAnyRole } from '@/lib/permissions';
import { PageLoading } from '@/components/ui/loading';

export function ProtectedRoute() {
  const { user, loading } = useAuth();
  const { pathname, search } = useLocation();
  if (loading) return <PageLoading />;
  if (!user) {
    const redirect = encodeURIComponent(`${pathname}${search}`);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  if (user.isTempPassword && !pathname.startsWith('/change-password')) {
    return <Navigate to="/change-password" replace />;
  }
  return <Outlet />;
}

export function PlatformProtectedRoute() {
  const { user, loading } = usePlatformAuth();
  const { pathname, search } = useLocation();
  if (loading) return <PageLoading />;
  if (!user) {
    const redirect = encodeURIComponent(`${pathname}${search}`);
    return <Navigate to={`/super-admin/login?redirect=${redirect}`} replace />;
  }
  if (user.isTempPassword && !pathname.startsWith('/super-admin/change-password')) {
    return <Navigate to="/super-admin/change-password" replace />;
  }
  return <Outlet />;
}

export function RoleRoute({ allowedRoles }: { allowedRoles: string[] }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAnyRole(user.roles, allowedRoles)) return <Navigate to="/unauthorized" replace />;
  return <Outlet />;
}

export function PermissionRoute({ anyOf }: { anyOf: string[] }) {
  const { user, loading, hasAnyRole: userHasAnyRole, hasAnyPermission } = useAuth();
  if (loading) return <PageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  // ORG_ADMIN keeps the complete administration access it had before RBAC.
  // Custom roles are checked using their assigned permission keys.
  if (!userHasAnyRole(['ORG_ADMIN', 'Admin']) && !hasAnyPermission(anyOf)) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <Outlet />;
}

export function AdminRoute() {
  return <RoleRoute allowedRoles={['Admin', 'ORG_ADMIN']} />;
}

export function EngineerRoute() {
  return <RoleRoute allowedRoles={['Engineer', 'SITE_ENGINEER']} />;
}
